import { z } from 'zod'

import { moneySchema, percentSchema } from '@/schemas/common'
import { isoDateSchema } from '@/schemas/portfolio'

/**
 * A stock alert as `GET /api/v5/alerts/` returns it, mapped to camelCase.
 *
 * Every field is backed by the wire — there is no client-only field:
 *
 * - `kind` / `condition` are `alert_type` and `direction` UP|DOWN, which the
 *   app words per kind: Above/Below, Rises/Falls (CHANGE), Crosses above/below
 *   (RSI, MACD, MA). The Alerts page creates PRICE and VOLUME; the chart rail
 *   creates all six.
 * - `trigger` is rupees for a price alert, a share count for a volume one, the
 *   size of the session's % move for CHANGE, the RSI level, or the MA length
 *   (20 or 50). A decimal string, or null for MACD, which has no level.
 * - `lastTraded` is the LTP, `lastVolume` today's volume, `lastChangePercent`
 *   today's % move — each null when there is no session price yet.
 *   `toGoPercent` is the backend's own figure, null for every kind but price
 *   and volume; the client never recomputes it.
 * - `channels` is a subset of `app` and `email`. v5 never sends SMS.
 * - `status` is derived server-side: ACTIVE (armed), FIRED (one-shot latch
 *   closed), EXPIRED (past `expiresOn`, never fired), PAUSED (disabled).
 * - `firedPrice` is the value that tripped it: the LTP (price), the volume,
 *   the % move (CHANGE), the RSI or MACD line (MACD can be negative), or the
 *   close (MA). Only the LIST call fills it; create/patch/rearm return null,
 *   which is why every mutation refetches the list instead of trusting its own
 *   response.
 */
export const alertKindSchema = z.enum(['price', 'volume', 'change', 'rsi', 'macd', 'ma'])
export const alertConditionSchema = z.enum(['above', 'below'])
export const alertChannelSchema = z.enum(['app', 'email'])
export const alertStatusSchema = z.enum(['ACTIVE', 'FIRED', 'EXPIRED', 'PAUSED'])
/** Which tab of `GET /alerts/?status=` — by expiry date, NOT by `status`. */
export const alertTabSchema = z.enum(['active', 'expired'])

export const alertSchema = z.object({
  id: z.number().int(),
  kind: alertKindSchema,
  condition: alertConditionSchema,
  symbol: z.string().min(1),
  company: z.string(),
  companyId: z.number().int(),
  /** The member's own label. `null` when they never set one. */
  name: z.string().nullable(),
  trigger: moneySchema.nullable(),
  lastTraded: moneySchema.nullable(),
  lastVolume: z.number().int().nullable(),
  lastChangePercent: percentSchema.nullable(),
  toGoPercent: percentSchema.nullable(),
  channels: z.array(alertChannelSchema),
  expiresOn: isoDateSchema,
  status: alertStatusSchema,
  /** ISO date-time. */
  firedAt: z.string().nullable(),
  firedPrice: moneySchema.nullable(),
  message: z.string().nullable(),
})

/** The two lengths an MA alert can watch: the averages mm-be stores. */
export const MA_LENGTHS = ['20', '50'] as const

const LEVEL_PATTERN = /^\d+(\.\d{1,2})?$/

const BLANK_LEVEL: Record<Exclude<AlertKind, 'macd'>, string> = {
  price: 'Enter a price.',
  volume: 'Enter a volume.',
  change: 'Enter a percent.',
  rsi: 'Enter an RSI level.',
  ma: 'Pick the 20 or 50 day average.',
}

/**
 * A typed level as a sentence of what is wrong with it, or `undefined` when mm-be would take it
 * (`services/alerts/alerts.py::_validate`): above zero with two decimals at most, then each
 * kind's own bound. MACD crosses its own signal line, so it has no level to check.
 */
export const alertLevelIssue = (kind: AlertKind, raw: string): string | undefined => {
  if (kind === 'macd') return undefined
  const level = raw.trim()
  if (level === '') return BLANK_LEVEL[kind]
  if (!LEVEL_PATTERN.test(level)) return 'Enter a level with at most two decimals.'
  const value = Number(level)
  if (value <= 0) return 'The level must be greater than zero.'
  if (kind === 'change' && value > 100) return 'A move is at most 100%.'
  if (kind === 'rsi' && value >= 100) return 'An RSI level is between 0 and 100.'
  if (kind === 'ma' && !MA_LENGTHS.some((length) => Number(length) === value)) {
    return 'Pick the 20 or 50 day average.'
  }
  return undefined
}

/**
 * What the create/edit form hands to a mutation. A MACD draft carries an empty `trigger`: the
 * level is not sent, and one typed for another kind must not ride along.
 */
export const alertDraftSchema = z
  .object({
    symbol: z.string().min(1, 'Choose a company.'),
    kind: alertKindSchema,
    condition: alertConditionSchema,
    trigger: z.string(),
    expiresOn: isoDateSchema,
    channels: z.array(alertChannelSchema).min(1, 'Pick at least one channel.'),
    name: z.string().trim().max(127),
  })
  .superRefine((draft, ctx) => {
    const message = alertLevelIssue(draft.kind, draft.trigger)
    if (message !== undefined) ctx.addIssue({ code: 'custom', path: ['trigger'], message })
  })

export type Alert = z.infer<typeof alertSchema>
export type AlertDraft = z.infer<typeof alertDraftSchema>
export type AlertKind = z.infer<typeof alertKindSchema>
export type AlertCondition = z.infer<typeof alertConditionSchema>
export type AlertChannel = z.infer<typeof alertChannelSchema>
export type AlertStatus = z.infer<typeof alertStatusSchema>
export type AlertTab = z.infer<typeof alertTabSchema>

/**
 * What a kind is called in a sentence. mm-be's own short names for the four
 * that are not a price or a volume, so a title never reads "NABIL rsi alert".
 */
export const ALERT_KIND_NAMES: Record<AlertKind, string> = {
  price: 'price',
  volume: 'volume',
  change: '% move',
  rsi: 'RSI',
  macd: 'MACD',
  ma: 'MA',
}

/** The card title: the member's label, else "<SYMBOL> price alert". */
export const alertTitle = (alert: Pick<Alert, 'name' | 'symbol' | 'kind'>): string =>
  alert.name?.trim() ? alert.name : `${alert.symbol} ${ALERT_KIND_NAMES[alert.kind]} alert`
